import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ROUTES, ROLES } from '../../consts/general';
import { useSelector } from 'react-redux';
import { detectEpcCounts, extractUniqueEpcTags } from './receivedUtils';

function formatDateTimeLocal(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function Received() {
  const { currentUser } = useSelector((state) => state.auth);
  const isPacker = currentUser?.role === ROLES.PACKER;
  
  // PO search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [poData, setPoData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [actualDateTime, setActualDateTime] = useState(formatDateTimeLocal(new Date()));
  const [isSaving, setIsSaving] = useState(false);
  
  // Scanner mode is always on for PO-level receiving
  const inputRef = useRef(null);
  const epcRawRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // EPC scanning state
  const [epcRaw, setEpcRaw] = useState('');
  const [epcDetectedCount, setEpcDetectedCount] = useState(null);
  const [epcUniqueCount, setEpcUniqueCount] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search for POs
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsSearching(true);
        const { data } = await axios.get('/api/v1/po-received-logs/search-pos', {
          params: { q: searchQuery.trim() }
        });
        setSearchResults(data || []);
        setShowDropdown(true);
      } catch (err) {
        console.error('Failed to search POs', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectPO = async (po) => {
    setShowDropdown(false);
    setSearchQuery('');
    
    try {
      const { data } = await axios.get('/api/v1/po-received-logs/search-po', {
        params: { po_number: po.po_number }
      });
      setPoData(data);
      setError(null);
      setMessage(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load Purchase Order');
      setPoData(null);
      setMessage(null);
    }
  };

  useEffect(() => {
    if (poData) {
      epcRawRef.current?.focus();
    }
  }, [poData]);

  // Reset scanner fields but stay on the same PO
  const resetScannerFields = () => {
    setEpcRaw('');
    setEpcDetectedCount(null);
    setEpcUniqueCount(null);
    setValidationResult(null);
    setActualDateTime(formatDateTimeLocal(new Date()));
    setError(null);
    setMessage(null);
    setTimeout(() => {
      epcRawRef.current?.focus();
    }, 100);
  };

  // Go back to PO search (exit current PO)
  const handleExitPO = () => {
    setSearchQuery('');
    setSearchResults([]);
    setPoData(null);
    setActualDateTime(formatDateTimeLocal(new Date()));
    setError(null);
    setMessage(null);
    setEpcRaw('');
    setEpcDetectedCount(null);
    setEpcUniqueCount(null);
    setValidationResult(null);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Refresh PO data to get updated cumulative counts
  const refreshPoData = async () => {
    if (!poData?.po_number) return;
    try {
      const { data } = await axios.get('/api/v1/po-received-logs/search-po', {
        params: { po_number: poData.po_number }
      });
      setPoData(data);
    } catch (err) {
      console.error('Failed to refresh PO data', err);
    }
  };

  // Save and continue scanning on the same PO
  const handleSaveAndContinue = async () => {
    if (!poData?.id || isSaving) return;
    
    const epc_hex_tags = extractUniqueEpcTags(epcRaw);
    
    // If no EPCs to save, just reset the fields
    if (epc_hex_tags.length === 0 || !validationResult?.totalMatched) {
      resetScannerFields();
      return;
    }

    try {
      setIsSaving(true);
      
      await axios.post('/api/v1/po-received-logs', {
        po_id: poData.id,
        date_received: new Date(actualDateTime).toISOString(),
        epc_hex_tags
      });
      
      // Refresh PO data to get updated cumulative counts
      await refreshPoData();
      
      // Reset scanner fields and focus for next scan
      resetScannerFields();
      setMessage('Saved! Ready for next scan.');
      
      // Clear success message after a moment
      setTimeout(() => {
        setMessage(null);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
      setMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  // Validate EPCs against the PO (debounced)
  useEffect(() => {
    if (!poData?.id) return;
    const uniqueTags = extractUniqueEpcTags(epcRaw);
    if (uniqueTags.length === 0) {
      setValidationResult(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsValidating(true);
        const { data } = await axios.post('/api/v1/po-received-logs/validate-epcs', {
          po_id: poData.id,
          epc_hex_tags: uniqueTags
        });
        setValidationResult(data);
      } catch (err) {
        console.error('Failed to validate EPCs', err);
        setValidationResult(null);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [epcRaw, poData?.id]);

  const handleEpcRawChange = (e) => {
    const value = e.target.value;
    setEpcRaw(value);
    const counts = detectEpcCounts(value);
    if (counts) {
      setEpcDetectedCount(counts.total);
      setEpcUniqueCount(counts.unique);
    } else {
      setEpcDetectedCount(null);
      setEpcUniqueCount(null);
      setValidationResult(null);
    }
  };

  // Save and exit to PO search
  const handleSaveAndExit = async () => {
    if (!poData?.id || isSaving) return;
    
    try {
      setIsSaving(true);
      const epc_hex_tags = extractUniqueEpcTags(epcRaw);
      
      if (epc_hex_tags.length === 0) {
        setError('No valid EPC tags detected. Please scan some items.');
        setMessage(null);
        return;
      }

      await axios.post('/api/v1/po-received-logs', {
        po_id: poData.id,
        date_received: new Date(actualDateTime).toISOString(),
        epc_hex_tags
      });
      
      setMessage('Saved successfully!');
      setError(null);
      
      // Auto-advance after short delay
      setTimeout(() => {
        handleExitPO();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
      setMessage(null);
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate totals from validation result
  const getTotalScannedThisSession = () => {
    if (!validationResult?.byProduct) return 0;
    return validationResult.byProduct.reduce((sum, p) => sum + (p.scanned_this_session || 0), 0);
  };

  return (
    <div className="p-8 flex items-center justify-center text-2xl relative">
      {!isPacker && (
        <Link
          to={ROUTES.RECEIVED_LOG}
          className="absolute top-4 left-4 text-blue-600 underline text-base"
        >
          View Received Log
        </Link>
      )}
      <div className="w-full max-w-4xl bg-white shadow rounded p-10 space-y-6">
        {poData || error ? (
          <div className="space-y-4">
            {poData && (
              <div className="space-y-6">
                {/* PO Header */}
                <div className="border-b pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-3xl font-bold">{poData.po_number}</h2>
                      <p className="text-gray-600 text-lg">{poData.vendor_name}</p>
                    </div>
                    <div className="text-right text-base">
                      <div className="text-gray-500">PO Date</div>
                      <div>{poData.po_date ? new Date(poData.po_date).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Expected Products Table */}
                <div>
                  <h3 className="font-semibold text-lg mb-3">Expected Products</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-base border">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-3 border text-left">SKU</th>
                          <th className="p-3 border text-left">Product</th>
                          <th className="p-3 border text-right">Expected</th>
                          <th className="p-3 border text-right">Previously Scanned</th>
                          <th className="p-3 border text-right">This Session</th>
                          <th className="p-3 border text-right">Total Scanned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {poData.line_items?.map((item, idx) => {
                          const validatedItem = validationResult?.byProduct?.find(
                            p => p.product_id === item.product_id
                          );
                          const scannedThisSession = validatedItem?.scanned_this_session || 0;
                          const cumulativeScanned = item.cumulative_scanned || 0;
                          const totalScanned = cumulativeScanned + scannedThisSession;
                          const variance = totalScanned - item.expected_quantity;
                          
                          return (
                            <tr key={idx} className="border-t">
                              <td className="p-3 border font-mono">{item.sku}</td>
                              <td className="p-3 border">{item.name}</td>
                              <td className="p-3 border text-right">{item.expected_quantity}</td>
                              <td className="p-3 border text-right text-gray-500">{cumulativeScanned}</td>
                              <td className="p-3 border text-right">
                                <span className={`font-semibold ${scannedThisSession > 0 ? 'text-green-600' : ''}`}>
                                  {scannedThisSession}
                                </span>
                              </td>
                              <td className="p-3 border text-right">
                                <span className={`font-semibold ${
                                  variance === 0 ? 'text-green-600' : 
                                  variance < 0 ? 'text-orange-600' : 'text-blue-600'
                                }`}>
                                  {totalScanned}
                                  {variance !== 0 && (
                                    <span className="text-sm ml-1">
                                      ({variance > 0 ? '+' : ''}{variance})
                                    </span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-semibold">
                          <td className="p-3 border" colSpan={2}>TOTAL</td>
                          <td className="p-3 border text-right">{poData.total_expected}</td>
                          <td className="p-3 border text-right text-gray-500">{poData.total_cumulative_scanned || 0}</td>
                          <td className="p-3 border text-right text-green-600">{getTotalScannedThisSession()}</td>
                          <td className="p-3 border text-right">
                            {(poData.total_cumulative_scanned || 0) + getTotalScannedThisSession()}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* EPC Scanner Section */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-medium">Detected EPC tags:</span>
                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-base font-semibold">
                      {epcDetectedCount != null ? epcDetectedCount : 0}
                    </span>
                    {epcUniqueCount != null && epcUniqueCount !== epcDetectedCount && (
                      <span className="text-sm text-gray-600">(unique: {epcUniqueCount})</span>
                    )}
                    {validationResult && (
                      <>
                        <span className="text-sm text-gray-600">→</span>
                        <span className="font-medium">Matched to PO:</span>
                        <span className={`inline-block px-2 py-0.5 rounded text-base font-semibold ${
                          validationResult.totalMatched > 0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {validationResult.totalMatched}
                        </span>
                        {validationResult.unmatched > 0 && (
                          <span className="text-sm text-gray-500">
                            ({validationResult.unmatched} ignored - not on this PO)
                          </span>
                        )}
                      </>
                    )}
                    {isValidating && (
                      <span className="text-sm text-gray-500 italic">Validating...</span>
                    )}
                  </div>
                  
                  <label className="block font-medium">RFID Scanner Input</label>
                  <textarea
                    ref={epcRawRef}
                    value={epcRaw}
                    onChange={handleEpcRawChange}
                    placeholder="Scan RFID labels here; we'll count full EPC-96 tags automatically"
                    rows={4}
                    className="border rounded p-4 w-full font-mono text-base"
                  />
                </div>

                {/* Received Date */}
                <div>
                  <label className="block font-medium">Received Date</label>
                  <input
                    type="datetime-local"
                    value={actualDateTime}
                    onChange={(e) => setActualDateTime(e.target.value)}
                    className="border rounded p-4 w-full"
                  />
                </div>
              </div>
            )}
            
            {message && <div className="text-green-600 font-semibold">{message}</div>}
            {error && <div className="text-red-500">{error}</div>}
            
            <div className="flex gap-2">
              <button
                onClick={handleExitPO}
                className="bg-gray-200 text-gray-700 px-6 py-4 rounded hover:bg-gray-300 transition-colors"
              >
                Change PO
              </button>
              {poData && (
                <>
                  <button
                    onClick={handleSaveAndContinue}
                    disabled={isSaving}
                    className="bg-blue-600 text-white px-6 py-4 rounded flex-1 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save & Scan Again'}
                  </button>
                  <button
                    onClick={handleSaveAndExit}
                    disabled={isSaving || !validationResult?.totalMatched}
                    className="bg-green-600 text-white px-6 py-4 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save & Done'}
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <label htmlFor="poSearch" className="block font-medium text-gray-700">
              Search for PO Number
            </label>
            <div className="relative" ref={dropdownRef}>
              <input
                id="poSearch"
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                placeholder="Type to search PO numbers..."
                className="border rounded p-4 w-full text-xl"
                autoComplete="off"
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                  Searching...
                </div>
              )}
              
              {/* Dropdown Results */}
              {showDropdown && searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto">
                  {searchResults.map((po) => (
                    <button
                      key={po.id}
                      type="button"
                      onClick={() => handleSelectPO(po)}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-bold text-lg">{po.po_number}</span>
                          <span className="text-gray-500 ml-3 text-base">{po.vendor_name}</span>
                        </div>
                        <div className="text-right text-sm text-gray-500">
                          <div>{po.status}</div>
                          {po.po_date && (
                            <div>{new Date(po.po_date).toLocaleDateString()}</div>
                          )}
                        </div>
                      </div>
                      {po.total_expected > 0 && (
                        <div className="text-sm text-gray-600 mt-1">
                          {po.total_expected} items expected
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {/* No Results Message */}
              {showDropdown && searchQuery.length >= 1 && searchResults.length === 0 && !isSearching && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-gray-500 text-center">
                  No POs found matching "{searchQuery}"
                </div>
              )}
            </div>
            <p className="text-gray-500 text-base text-center">
              Type to search, then click a PO to begin receiving
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Received;
